import type { DocMigration } from "../docMigration";

/**
 *  This migration targets the following fields in order to update
 *  the id field name of each stored entity handle
 *
 * Doc.hidden
 * Doc.locked
 * Foundation.perimeter
 * Foundation.include
 * Foundation.exclude
 * ElectricalRoomLayout.handle
 */

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_262: DocMigration = {
  maxSchemaVersion: "0.0.262",
  description: "Adds a default roofSideOverrides to each roof",
  apply(json: any) {
    // Doc.hidden
    if (Object.keys(json.hidden ?? {}).length > 0) {
      for (const hash in json.hidden) {
        const handle = json.hidden[hash];
        json.hidden[hash] = rewriteEntityHandleField(handle);
      }
    }

    // Doc.locked
    if (Object.keys(json.locked ?? {}).length > 0) {
      for (const hash in json.locked) {
        const handle = json.locked[hash];
        json.locked[hash] = rewriteEntityHandleField(handle);
      }
    }

    for (const foundationId in json.foundations) {
      const foundation = json.foundations[foundationId];
      // Foundation.perimeter
      if (foundation.perimeter) {
        foundation.perimeter = rewriteEntityHandleField(foundation.perimeter);
      }

      // Foundation.include
      for (const blockId in foundation.include) {
        foundation.include[blockId] = rewriteEntityHandleField(foundation.include[blockId]);
      }

      // Foundation.exclude
      for (const blockId in foundation.exclude) {
        foundation.exclude[blockId] = rewriteEntityHandleField(foundation.exclude[blockId]);
      }
    }

    // ElectricalRoomLayout.handle
    for (const loadId in json.electricalSystem?.loads) {
      const load = json.electricalSystem.loads[loadId];
      load.handle = rewriteEntityHandleField(load.handle);
    }

    return json;
  },
};

export default migration_0_0_262;

function rewriteEntityHandleField(handle: any) {
  switch (handle?.type) {
    case "GROUP":
      return {
        type: "GROUP",
        id: handle.groupId,
      };

    case "LEVEL":
      return {
        type: "LEVEL",
        id: handle.levelId,
      };

    case "BLOCK":
      return {
        type: "BLOCK",
        id: handle.blockId,
      };

    case "ROOM":
      return {
        type: "ROOM",
        id: handle.roomId,
      };

    case "ROOM_LAYOUT":
      return {
        type: "ROOM_LAYOUT",
        id: handle.roomLayoutId,
      };

    case "ROOM_LAYOUT_INSTANCE":
      return {
        type: "ROOM_LAYOUT_INSTANCE",
        id: handle.roomLayoutInstanceId,
      };

    case "STAIR_SYSTEM":
      return {
        type: "STAIR_SYSTEM",
        id: handle.stairSystemId,
      };

    case "STAIR_BLOCK":
      return {
        type: "STAIR_BLOCK",
        id: handle.stairBlockId,
      };

    case "RAILING":
      return {
        type: "RAILING",
        id: handle.railingId,
      };

    case "SITE":
      return {
        type: "SITE",
        id: handle.siteId,
      };

    case "ROOF_SYSTEM":
      return {
        type: "ROOF_SYSTEM",
        id: handle.roofSystemId,
      };

    case "ROOF":
      return {
        type: "ROOF",
        id: handle.roofId,
      };

    case "ROOF_SIDE":
      return {
        type: "ROOF_SIDE",
        id: handle.roofId,
        side: handle.side,
      };

    case "FOUNDATION_SYSTEM":
      return {
        type: "FOUNDATION_SYSTEM",
        id: handle.foundationSystemId,
      };

    case "FOUNDATION":
      return {
        type: "FOUNDATION",
        id: handle.foundationId,
      };

    case "FOUNDATION_PATTERN": {
      const foundationPatternHandle: any = {
        type: "FOUNDATION_PATTERN",
        id: handle.foundationPatternId,
      };

      if (handle.parent) {
        foundationPatternHandle.parent = rewriteEntityHandleField(handle.parent);
      }

      return foundationPatternHandle;
    }
    case "UNDERLAY":
      return {
        type: "UNDERLAY",
        id: handle.underlayId,
      };

    case "REFERENCE_GEOM":
      return {
        type: "REFERENCE_GEOM",
        id: handle.geomId,
      };

    case "ELEVATION":
      return {
        type: "ELEVATION",
        id: handle.elevationId,
      };

    case "PALETTE":
      return {
        type: "PALETTE",
        id: handle.paletteId,
      };

    case "SETTINGS":
      return {
        type: "SETTINGS",
        id: handle.settingsId,
      };

    case "OPTION_TEMPLATE":
      return {
        type: "OPTION_TEMPLATE",
        id: handle.optionTemplateId,
      };

    case "OPTION_SET_OVERRIDES":
      return {
        type: "OPTION_SET_OVERRIDES",
        id: handle.optionSetOverridesId,
      };

    case "SHEET":
      return {
        type: "SHEET",
        id: handle.sheetId,
      };

    case "DRAWING_REF":
      return {
        type: "DRAWING_REF",
        id: handle.drawingRefId,
      };

    case "OPTION_SET":
      return {
        type: "OPTION_SET",
        id: handle.optionSetId,
      };

    case "AUX_OPTION_SET":
      return {
        type: "AUX_OPTION_SET",
        id: handle.optionSetId,
      };

    case "PROP":
      return {
        type: "PROP",
        id: handle.propId,
      };

    case "CASEWORK":
      return {
        type: "CASEWORK",
        id: handle.caseworkId,
      };

    case "CEILING":
      return {
        type: "CEILING",
        id: handle.ceilingId,
      };

    case "OPENING":
      return {
        type: "OPENING",
        id: handle.openingId,
      };

    case "FLOOR":
      return {
        type: "FLOOR",
        id: handle.floorId,
      };

    case "ANNOTATION":
      return {
        type: "ANNOTATION",
        id: handle.annotationId,
      };

    case "ANNOTATION_TEMPLATE":
      return {
        type: "ANNOTATION_TEMPLATE",
        id: handle.annotationTemplateId,
        settingsHandle: rewriteEntityHandleField(handle.settingsHandle),
      };

    case "ANNOTATION_TEMPLATE_COMPONENT":
      return {
        type: "ANNOTATION_TEMPLATE_COMPONENT",
        id: handle.componentId,
        annotationTemplateHandle: rewriteEntityHandleField(handle.annotationTemplateHandle),
      };

    case "FACADE":
      return {
        type: "FACADE",
        id: handle.facadeId,
      };

    case "GABLE_OPENING":
      return {
        type: "GABLE_OPENING",
        id: handle.gableOpeningId,
      };

    case "SKYLIGHT":
      return {
        type: "SKYLIGHT",
        id: handle.skylightId,
      };

    case "DORMER":
      return {
        type: "DORMER",
        id: handle.dormerId,
      };

    case "MODIFIER":
      return {
        type: "MODIFIER",
        id: handle.modifierId,
      };

    case "COLUMN": {
      const columnHandle: any = {
        type: "COLUMN",
        id: handle.columnId,
      };

      if (handle.parent) {
        if (handle.type === "ROOM") {
          columnHandle.parent = rewriteEntityHandleField(handle.parent);
        } else {
          columnHandle.parent.parent = rewriteEntityHandleField(handle.parent.parent);
        }
      }

      return columnHandle;
    }

    case "MEASUREMENT":
      return {
        type: "MEASUREMENT",
        id: handle.measurementId,
      };

    default: {
      return handle;
    }
  }
}
